import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import {
  Carousel,
  Spinner,
  Button,
} from 'react-bootstrap';
import BasePage from '../../../BasePage/BasePage';
import Icon from '../../../../atoms/Icon/Icon';
import Sidebar from '../../../../molecules/Sidebar/Sidebar';
import {
  fetchContextsInitAction,
  updateSelectedContextAction,
} from '../../../../../actions/instances/context';
import { loadContextsMetadata } from '../../../../../helpers/metadata';
import DeleteModal from '../../../../molecules/DeleteModal/DeleteModal';
import Api from '../../../../../services/Api';
import { publishContext } from '../../../../../helpers/publish';
import InstanceMetadata from '../../../../Organisms/InstanceMetadata/InstanceMetadata';
import BuildingsList from '../../../../Organisms/BuildingsList/BuildingsList';
import DocumentsList from '../../../../Organisms/DocumentsList/DocumentsList';
import CitationList from '../../../../Organisms/CitationList/CitationList';
import './EditViewContexts.scss';

const EditViewContexts = () => {
  const {
    selectedContext,
  } = useSelector(state => state.contexts);
  const { user } = useSelector(state => state.auth);
  const dispatch = useDispatch();
  const history = useHistory();
  const [buildings, setBuildings] = useState([]);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [isDeleteModalShown, setIsDeleteModalShown] = useState(false);
  useEffect(() => {
    if (selectedContext) {
      loadContextsMetadata(selectedContext.id).then(metadata => setBuildings(metadata.buildings));
    }
  }, []);
  if (!selectedContext) {
    history.push('/instances/building-contexts');
  }
  const deleteContext = () => {
    setIsDeleting(true);
    Api.delete(`/instances/building-contexts/${selectedContext.id}`).then((response) => {
      if (response.status === 204) {
        dispatch(fetchContextsInitAction());
        setTimeout(() => {
          history.goBack();
          setIsDeleting(false);
        }, 500);
      } else {
        setIsDeleting(false);
      }
    });
  };
  return (
    <BasePage>
      <DeleteModal
        onDelete={deleteContext}
        isShown={isDeleteModalShown}
        isLoading={isDeleting}
        onClose={() => setIsDeleteModalShown(false)}
        isAllowed
      >
        Are you sure you want to delete
        {' '}
        <strong>{selectedContext.name}</strong>
        {'? '}
        <br />
        This action cannot be undone.
      </DeleteModal>
      <div className="col-md-3">
        <div className="row mt-4">
          <Carousel className="w-100">
            {/* eslint-disable-next-line max-len */}
            {selectedContext && selectedContext.images.length > 0 ? selectedContext.images.map(image => (
              <Carousel.Item>
                <img
                  className="d-block w-100 img-fluid"
                  src={image.url}
                  alt={image.label}
                  style={{
                    maxHeight: '400px',
                    objectFit: 'cover',
                  }}
                />
              </Carousel.Item>
            )) : (
              <img src="/images/default.jpg" alt={selectedContext.name} className="img-fluid" />
            )}
          </Carousel>
        </div>
        <div className="col-md-12 mt-2 d-grid gap-2">
          {user.role_id !== 3 && (
            <Button
              variant={selectedContext.in_review === 1 ? 'success' : 'warning'}
              onClick={async () => {
                setIsPublishing(true);
                const component = await publishContext(
                  selectedContext.id,
                  selectedContext.in_review,
                );
                dispatch(updateSelectedContextAction(component));
                dispatch(fetchContextsInitAction());
                setIsPublishing(false);
              }}
            >
              {isPublishing ? (
                <>
                  <Spinner
                    as="span"
                    animation="grow"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                  />
                  <span className="ms-2">Loading...</span>
                </>
              ) : (
                <>
                  <span className={`ms-2 ${selectedContext.in_review === 0 ? 'text-white' : ''}`}>
                    {selectedContext.in_review === 1 ? 'Approve' : 'Disapprove'}
                  </span>
                </>
              )}
            </Button>
          )}
          <Button
            variant="dark"
            onClick={() => {
              dispatch(updateSelectedContextAction(selectedContext));
              history.push('/instances/building-contexts/edit');
            }}
          >
            <Icon icon="edit" />
            <span className="ms-2">Edit</span>
          </Button>
          <Button variant="dark" onClick={() => history.goBack()}>
            <Icon icon="times" />
            <span className="ms-2">Cancel</span>
          </Button>
          {user.role_id !== 3 && (
            <Button variant="danger" onClick={() => setIsDeleteModalShown(true)}>
              {isDeleting ? (
                <>
                  <Spinner
                    as="span"
                    animation="grow"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                  />
                  <span className="ms-2">Loading...</span>
                </>
              ) : (
                <>
                  <Icon icon="trash" />
                  <span className="ms-2">Delete</span>
                </>
              )}
            </Button>
          )}
        </div>
      </div>
      <div className="col-md-6">
        <div className="row d-flex align-items-center mb-3">
          <h3>{selectedContext.type.name}</h3>
        </div>
        <div className="row border-top border-bottom mt-2 p-2">
          <div className="col-md-12">
            <div className="border-end d-inline pe-2 me-2">
              <span className="me-1">
                <Icon icon="calendar" color="#000" />
              </span>
              {moment(selectedContext.cerated_at).format('DD-MM-YYYY')}
            </div>
            <div className="d-inline">
              {selectedContext.tags.map(tag => (
                <span className="me-2 border-end pe-2">
                  <Icon icon="tag" color="#000" />
                  <span className="ms-2">
                    {tag.name}
                  </span>
                </span>
              ))}
            </div>
            <div className="d-inline border-end d-inline pe-2 me-2">
              <Icon icon="map" color="#000" />
              <span className="ms-2">
                {`${selectedContext.city.name}, ${selectedContext.city.country.name} [${selectedContext.city.code}-${selectedContext.city.country.code}]`}
              </span>
            </div>
            <div className="d-inline">
              <Icon icon="globe" color="#000" />
              <span className="ms-2">
                {`${selectedContext.dynasty.name} [${selectedContext.dynasty.code}]`}
              </span>
            </div>
          </div>
        </div>
        <InstanceMetadata instance={selectedContext} title="Context Metadata" />
        <BuildingsList buildings={buildings} />
        <DocumentsList documents={selectedContext.documents} title="Documents" />
        <CitationList citations={selectedContext.citation} title="Citations" />
      </div>
      <div className="col-md-3 mt-5">
        <Sidebar />
      </div>
    </BasePage>
  );
};

export default EditViewContexts;
